import { useDebouncedState } from '@mantine/hooks'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'

import { useQueryGetAllItems } from '@flecto/core/saas'
import { type CoreItem, type CoreBundleItemPayload } from '@flecto/types'

const INITIAL_LIMIT = 50

export type UseBundleItemsFilterProps = {
  itemsInBundle?: Array<CoreBundleItemPayload>
}

export function useBundleItemsFilter({
  itemsInBundle,
}: UseBundleItemsFilterProps) {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [searchTerm, setSearchTerm] = useDebouncedState('', 300)

  const [page, setPage] = useState(1)
  const offset = (page - 1) * INITIAL_LIMIT

  const { data, isLoading, isFetching, isError, error, refetch } =
    useQueryGetAllItems({
      q: searchTerm,
      limit: INITIAL_LIMIT,
      offset,
      order: 'desc',
      enabled: true,
    })

  const [allFetchedItems, setAllFetchedItems] = useState<CoreItem[]>([])

  useEffect(() => {
    if (data?.item) {
      const bundleIds = itemsInBundle?.map((item) => item.item_id) || []
      const filteredData = itemsInBundle?.length
        ? (data.item as CoreItem[]).filter(
            (item) => !bundleIds.includes(item.id)
          )
        : (data.item as CoreItem[])

      setAllFetchedItems((prev) =>
        page === 1 ? filteredData : [...prev, ...filteredData]
      )
    }
  }, [data, page, itemsInBundle])

  const filteredItems = useMemo(() => {
    const lower = searchTerm.toLowerCase()

    return allFetchedItems.filter(
      (item) =>
        !lower ||
        item.title?.toLowerCase().includes(lower) ||
        item.categories?.some((c) => c.toLowerCase().includes(lower))
    )
  }, [allFetchedItems, searchTerm])

  const groupedItems = useMemo(() => {
    const acc = new Map<string, CoreItem[]>()

    filteredItems.forEach((item) => {
      const group = item.categories?.join(' > ') ?? ''
      if (!acc.has(group)) acc.set(group, [])
      acc.get(group)!.push(item)
    })

    return acc
  }, [filteredItems])

  const hasMore = useMemo(() => {
    if (!data?.item) return false
    return (data.item as CoreItem[]).length === INITIAL_LIMIT
  }, [data])

  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) setPage((p) => p + 1)
  }, [hasMore, isFetching])

  const clearSearch = useCallback(() => {
    setSearchTerm('')
    if (searchInputRef.current) searchInputRef.current.value = ''
    setPage(1)
  }, [setSearchTerm])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.currentTarget.value)
      setPage(1)
    },
    [setSearchTerm]
  )

  return useMemo(
    () => ({
      searchInputRef,
      searchTerm,
      groupedItems,
      filteredItems,
      hasMore,
      isLoading,
      isFetching,
      isError,
      error,
      handleSearchChange,
      clearSearch,
      loadMore,
      refetch,
    }),
    [
      searchTerm,
      groupedItems,
      filteredItems,
      hasMore,
      isLoading,
      isFetching,
      isError,
      error,
      clearSearch,
      handleSearchChange,
      loadMore,
      refetch,
    ]
  )
}
